// src/utils/resumeUtils.ts
import { TaskStatus, TaskStatusInfo, getTaskStatusInfo, hasResults } from './taskStatus.ts';

// Export the Resume interface
export interface Resume {
  id: string;
  original_filename: string;
  upload_timestamp: string;
  analysis_status: TaskStatus;
  public_url?: string;
  is_default?: boolean; // For future backend support
  // Optional editable display name captured in the frontend until backend supports it
  custom_name?: string;
}

/**
 * Resume-specific status display information with custom labels
 */
export const getResumeStatusInfo = (status: TaskStatus): TaskStatusInfo & { icon: string } => {
  const baseInfo = getTaskStatusInfo(status);
  
  // Resume-specific labels and icons
  const resumeCustomization = {
    'queued': { 
      label: 'Brewing up...', 
      icon: 'cafeOutline' 
    },
    'processing': { 
      label: 'AI at Work...', 
      icon: 'hardwareChipOutline' 
    },
    'completed': { 
      label: 'Analysis Completed', 
      icon: 'checkmarkCircle' 
    },
    'failed': { 
      label: 'Analysis Failed', 
      icon: 'alertCircleOutline' 
    },
    'retrying': { 
      label: 'Ready to Retry', 
      icon: 'alertCircleOutline' 
    }
  };

  const customization = resumeCustomization[status] || { 
    label: baseInfo.label, 
    icon: 'helpCircleOutline' 
  };

  return {
    ...baseInfo,
    label: customization.label,
    icon: customization.icon
  };
};

/**
 * Check if resume analysis can be viewed
 */
export const canViewAnalysis = (resume: Resume): boolean => {
  return hasResults(resume.analysis_status);
};

/**
 * Get resumes that need polling
 */
export const getResumesNeedingPolling = (resumes: Resume[]): Resume[] => {
  return resumes.filter(resume => resume.analysis_status === 'queued' || 
                                  resume.analysis_status === 'processing' || 
                                  resume.analysis_status === 'retrying');
};

/**
 * Check if any resumes have active statuses (for polling decision)
 */
export const hasActiveResumeStatuses = (resumes: Resume[]): boolean => {
  return resumes.some(resume =>
    resume.analysis_status === 'queued' ||
    resume.analysis_status === 'processing' ||
    resume.analysis_status === 'retrying'
  );
};

/**
 * Get the default resume to display (primary logic)
 * 1. If a resume is marked as default, return that one
 * 2. If none is default, return the most recently uploaded one with completed analysis
 * 3. If no completed analysis, return null
 */
export const getDefaultResume = (resumes: Resume[]): Resume | null => {
  if (!resumes || resumes.length === 0) return null;

  // First, try to find one marked as default with completed analysis
  const defaultResume = resumes.find(resume =>
    resume.is_default && resume.analysis_status === 'completed'
  );
  if (defaultResume) return defaultResume;

  // Fallback: find the most recently uploaded with completed analysis
  const completedResumes = resumes.filter(resume => resume.analysis_status === 'completed');
  if (completedResumes.length === 0) return null;

  // Sort by upload timestamp (most recent first)
  completedResumes.sort((a, b) =>
    new Date(b.upload_timestamp).getTime() - new Date(a.upload_timestamp).getTime()
  );

  return completedResumes[0];
};